// App.tsx
import React, { useEffect, useRef, useState } from 'react';
import { NavigationContainer, NavigationContainerRef } from '@react-navigation/native';
import MainTabNavigator from './navigators/MainTabNavigator';
import { createStackNavigator } from '@react-navigation/stack';
import { AuthProvider } from './context/AuthContext';
import { PaperProvider } from './context/SafeProviders';
import { Platform } from 'react-native';
import * as SplashScreen from 'expo-splash-screen';
import { useFonts, PressStart2P_400Regular } from '@expo-google-fonts/press-start-2p';
import { VideoProvider } from './context/VideoContext';
import { RefetchProvider } from './context/RefetchContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { RootStackParamList } from './linkingConfig';
import { View } from './context/SafeComponents';
import { ModalManagerProvider } from './context/ModalManagerContext';
import { ManagedModal } from './components/ManagedModal/ManagedModal';
import './warningFilter';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import { enableScreens } from 'react-native-screens';
import { useAuthenticatedApi } from './hooks/user/useAuthenticatedApi';
import { LoginModalProvider, useLoginModal } from './context/LoginModalContext';
import AuthModal from './components/AuthModal/AuthModal';
import 'react-native-gesture-handler';

try {
  enableScreens();
} catch (e) {
  console.error("Failed to enable screens:", e);
}

const RootStack = createStackNavigator<RootStackParamList>();

const AppContent = () => {

  const navigationRef = useRef<NavigationContainerRef<any>>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const { authenticatedApiCall } = useAuthenticatedApi();
  const { shouldShowLoginModal, setShouldShowLoginModal } = useLoginModal();

  useEffect(() => {
    if (Platform.OS === 'web') {
      document.title = 'Clipdle';
    }
  }, []);

  const checkIfAdmin = async () => {
    try {
      const response = await authenticatedApiCall('admin/check', 'GET');
      const data = response.data;
      setIsAdmin(data ? data.isAdmin : false);
    } catch (error) {
      setIsAdmin(false);
    }
  };

  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const handleKeyDown = (event: { ctrlKey: any; altKey: any; key: string; }) => {
        if (event.ctrlKey && event.altKey && event.key === 'a') {  
          checkIfAdmin();
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, []);

  const queryClient = new QueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      <View style={{ flex: 1, width: '100%', backgroundColor: 'black' }}>
        <RefetchProvider>
          <PaperProvider>
            <VideoProvider>
            <NavigationContainer 
              ref={navigationRef}
              documentTitle={{
                enabled: true,
                formatter: () => 'Clipdle',
              }}
            >
                <ManagedModal id="login-modal" visible={shouldShowLoginModal}>
                  <AuthModal 
                    visible={shouldShowLoginModal}  // Add visible prop
                    onClose={() => setShouldShowLoginModal(false)} 
                  />
                </ManagedModal>
                <RootStack.Navigator screenOptions={{ headerShown: false }} initialRouteName="LauncherScreen">
                  <RootStack.Screen name="MainTab" component={MainTabNavigator} />
                </RootStack.Navigator>
              </NavigationContainer>
            </VideoProvider>
          </PaperProvider>
        </RefetchProvider>
      </View>
    </QueryClientProvider>
  );
};

const App = () => {
  
  const [fontsLoaded, fontError] = useFonts({
    PressStart2P_400Regular,
  });

  useEffect(() => {
    if (fontsLoaded || fontError) {
      SplashScreen.hideAsync().catch(e => {
      });
    }
  }, [fontsLoaded, fontError]);

  return <AppContent />;
};

const AppWrapper = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <LoginModalProvider>
          <ModalManagerProvider>
            <App />
          </ModalManagerProvider>
        </LoginModalProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default AppWrapper;
